import * as React from "react";
import {
  Box,
  Button,
  Field as ChakraField,
  HStack,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  Formik,
  Form,
  Field,
  FieldProps,
  FormikProps,
  FormikHelpers,
} from "formik";

import {
  complaintStatusRequest,
  complaintStatusReset,
} from "store/bbps/Complaint/bbpsComplaintActions";
import { RootState } from "store/rootReducer";
import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";

type ViewComplaintStatusFormValues = {
  orderId: string;
};

const FloatingInputField: React.FC<
  FieldProps & {
    label: string;
    maxLength?: number;
    required?: boolean;
  }
> = ({ label, field, form, maxLength, required }) => {
  const [focus, setFocus] = React.useState(false);
  const value = String(field.value ?? "");
  const active = focus || value.length > 0;

  const error =
    (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name]
      ? String((form.errors as any)[field.name])
      : undefined;

  return (
    <ChakraField.Root invalid={!!error}>
      <Box position="relative">
        <Input
          {...field}
          value={value}
          maxLength={maxLength}
          onFocus={() => setFocus(true)}
          onBlur={(e) => {
            setFocus(false);
            field.onBlur(e);
          }}
          h="52px"
          pt="18px"
          bg="white"
          borderColor="blackAlpha.200"
          _hover={{ borderColor: "blackAlpha.300" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
          required={required}
          autoComplete="new-password"
        />

        <Text
          position="absolute"
          left="12px"
          top={active ? "8px" : "50%"}
          transform={active ? "translateY(0)" : "translateY(-50%)"}
          fontSize={active ? "xs" : "sm"}
          color={active ? "gray.600" : "gray.500"}
          pointerEvents="none"
          transition="all 120ms ease"
          bg="white"
          px="6px"
        >
          {label}
        </Text>
      </Box>

      {error ? <ChakraField.ErrorText>{error}</ChakraField.ErrorText> : null}
    </ChakraField.Root>
  );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between">
      <Text fontSize="sm" color="gray.600">
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color="gray.800"
        textAlign="right"
      >
        {value}
      </Text>
    </HStack>
  );
};

export const ViewComplaintStatusPage: React.FC = () => {
  const dispatch = useDispatch();
  const statusBox = useSelector((s: RootState) => s.bbpsComplaint.status);

  React.useEffect(() => {
    dispatch(complaintStatusReset());
  }, [dispatch]);

  const initialValues: ViewComplaintStatusFormValues = { orderId: "" };

  const validate = (values: ViewComplaintStatusFormValues) => {
    const errors: Partial<Record<keyof ViewComplaintStatusFormValues, string>> =
      {};
    const orderId = values.orderId?.trim() ?? "";

    if (!orderId) errors.orderId = "Transaction ID is required.";
    if (orderId.length > 30)
      errors.orderId = "Transaction ID must be <= 30 characters.";

    return errors;
  };

  const onSubmit = (
    values: ViewComplaintStatusFormValues,
    actions: FormikHelpers<ViewComplaintStatusFormValues>,
  ) => {
    // IMPORTANT: Formik sets isSubmitting=true before calling onSubmit,
    // so if we return early, we must set it back to false.
    if (statusBox.loading) {
      actions.setSubmitting(false);
      return;
    }

    dispatch(complaintStatusReset());
    dispatch(complaintStatusRequest({ orderId: values.orderId.trim() }));

    // Redux/Saga is the async owner; release Formik immediately
    actions.setSubmitting(false);
  };

  return (
    <BbpsComplainLayout title="View Complaint Status" backTo="/bbps">
      <Formik<ViewComplaintStatusFormValues>
        initialValues={initialValues}
        validate={validate}
        validateOnBlur
        validateOnChange
        onSubmit={onSubmit}
      >
        {(fp: FormikProps<ViewComplaintStatusFormValues>) => {
          const canSubmit =
            fp.isValid &&
            !statusBox.loading &&
            fp.values.orderId.trim().length > 0;

          return (
            <Form>
              <VStack align="stretch" gap={4}>
                <Field name="orderId">
                  {(p: FieldProps) => (
                    <FloatingInputField
                      {...p}
                      label="Transaction ID"
                      maxLength={30}
                      required
                    />
                  )}
                </Field>

                <Button
                  type="submit"
                  h="48px"
                  rounded="lg"
                  loading={statusBox.loading} // ✅ only redux loading
                  disabled={!canSubmit} // ✅ no fp.isSubmitting
                >
                  View Status
                </Button>

                {statusBox.error && (
                  <Box
                    p={3}
                    rounded="lg"
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.100"
                  >
                    <Text fontWeight="semibold" color="red.700">
                      {statusBox.error}
                    </Text>
                    {statusBox.statusCode && (
                      <Text fontSize="sm" color="red.600" mt={1}>
                        StatusCode: {statusBox.statusCode}
                      </Text>
                    )}
                  </Box>
                )}

                {statusBox.data && (
                  <Box
                    p={4}
                    rounded="xl"
                    bg="blue.50"
                    borderWidth="1px"
                    borderColor="blue.100"
                  >
                    <Text fontWeight="bold" color="blue.800">
                      Complaint Status
                    </Text>

                    <VStack align="stretch" mt={3} gap={2}>
                      <Row label="Ticket ID" value={statusBox.data.ticketId} />
                      <Row
                        label="Ticket Status"
                        value={statusBox.data.ticketStatus}
                      />
                      <Row
                        label="Reason"
                        value={statusBox.data.responseReason}
                      />
                      <Row
                        label="Ticket Type"
                        value={statusBox.data.ticketType}
                      />
                      {statusBox.data.description && (
                        <Row
                          label="Description"
                          value={statusBox.data.description}
                        />
                      )}
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Form>
          );
        }}
      </Formik>
    </BbpsComplainLayout>
  );
};

export default ViewComplaintStatusPage;

// import * as React from "react";
// import {
//   Box,
//   Button,
//   Field as ChakraField,
//   HStack,
//   Input,
//   Text,
//   VStack,
// } from "@chakra-ui/react";
// import { useDispatch, useSelector } from "react-redux";
// import { Formik, Form, Field, FieldProps, FormikProps } from "formik";

// import {
//   complaintStatusRequest,
//   complaintStatusReset,
// } from "store/bbps/Complaint/bbpsComplaintActions";
// import { RootState } from "store/rootReducer";
// import { BbpsComplainLayout } from "layouts/BbpsComplainLayout/BbpsComplainLayout";

// type ViewComplaintStatusFormValues = {
//   orderId: string;
// };

// const FloatingInputField: React.FC<
//   FieldProps & {
//     label: string;
//     maxLength?: number;
//     required?: boolean;
//   }
// > = ({ label, field, form, maxLength, required }) => {
//   const [focus, setFocus] = React.useState(false);
//   const value = String(field.value ?? "");
//   const active = focus || value.length > 0;

//   const error =
//     (form.touched as any)?.[field.name] && (form.errors as any)?.[field.name]
//       ? String((form.errors as any)[field.name])
//       : undefined;

//   return (
//     <ChakraField.Root invalid={!!error}>
//       <Box position="relative">
//         <Input
//           {...field}
//           value={value}
//           maxLength={maxLength}
//           onFocus={() => setFocus(true)}
//           onBlur={(e) => {
//             setFocus(false);
//             field.onBlur(e);
//           }}
//           h="52px"
//           pt="18px"
//           bg="white"
//           borderColor="blackAlpha.200"
//           _hover={{ borderColor: "blackAlpha.300" }}
//           _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px" }}
//           required={required}
//           autoComplete="new-password"
//         />

//         <Text
//           position="absolute"
//           left="12px"
//           top={active ? "8px" : "50%"}
//           transform={active ? "translateY(0)" : "translateY(-50%)"}
//           fontSize={active ? "xs" : "sm"}
//           color={active ? "gray.600" : "gray.500"}
//           pointerEvents="none"
//           transition="all 120ms ease"
//           bg="white"
//           px="6px"
//         >
//           {label}
//         </Text>
//       </Box>

//       {error ? <ChakraField.ErrorText>{error}</ChakraField.ErrorText> : null}
//     </ChakraField.Root>
//   );
// };

// const Row: React.FC<{ label: string; value?: string | null }> = ({
//   label,
//   value,
// }) => {
//   if (!value) return null;
//   return (
//     <HStack justify="space-between">
//       <Text fontSize="sm" color="gray.600">
//         {label}
//       </Text>
//       <Text
//         fontSize="sm"
//         fontWeight="semibold"
//         color="gray.800"
//         textAlign="right"
//       >
//         {value}
//       </Text>
//     </HStack>
//   );
// };

// export const ViewComplaintStatusPage: React.FC = () => {
//   const dispatch = useDispatch();
//   const statusBox = useSelector((s: RootState) => s.bbpsComplaint.status);

//   console.log("statusBox.loading ", statusBox.loading);
//   React.useEffect(() => {
//     dispatch(complaintStatusReset());
//   },[dispatch]);
//   const initialValues: ViewComplaintStatusFormValues = { orderId: "" };

//   const validate = (values: ViewComplaintStatusFormValues) => {
//     const errors: Partial<Record<keyof ViewComplaintStatusFormValues, string>> =
//       {};
//     const orderId = values.orderId?.trim() ?? "";

//     if (!orderId) errors.orderId = "Transaction ID is required.";
//     if (orderId.length > 30)
//       errors.orderId = "Transaction ID must be <= 30 characters.";

//     return errors;
//   };

//   const onSubmit = (values: ViewComplaintStatusFormValues) => {
//     if (statusBox.loading) return;

//     dispatch(complaintStatusReset());
//     dispatch(complaintStatusRequest({ orderId: values.orderId.trim() }));
//   };

//   return (
//     <BbpsComplainLayout title="View Complaint Status" backTo="/bbps">
//       <Formik<ViewComplaintStatusFormValues>
//         initialValues={initialValues}
//         validate={validate}
//         validateOnBlur
//         validateOnChange
//         onSubmit={onSubmit}
//       >
//         {(fp: FormikProps<ViewComplaintStatusFormValues>) => {
//           const canSubmit =
//             fp.isValid &&
//             !statusBox.loading &&
//             !fp.isSubmitting &&
//             fp.values.orderId.trim().length > 0;

//           return (
//             <Form>
//               <VStack align="stretch" gap={4}>
//                 <Field name="orderId">
//                   {(p: FieldProps) => (
//                     <FloatingInputField
//                       {...p}
//                       label="Transaction ID"
//                       maxLength={30}
//                       required
//                     />
//                   )}
//                 </Field>

//                 <Button
//                   type="submit"
//                   h="48px"
//                   rounded="lg"
//                   loading={statusBox.loading || fp.isSubmitting}
//                   disabled={!canSubmit}
//                 >
//                   View Status
//                 </Button>

//                 {/* Result */}
//                 {statusBox.error && (
//                   <Box
//                     p={3}
//                     rounded="lg"
//                     bg="red.50"
//                     borderWidth="1px"
//                     borderColor="red.100"
//                   >
//                     <Text fontWeight="semibold" color="red.700">
//                       {statusBox.error}
//                     </Text>
//                     {statusBox.statusCode && (
//                       <Text fontSize="sm" color="red.600" mt={1}>
//                         StatusCode: {statusBox.statusCode}
//                       </Text>
//                     )}
//                   </Box>
//                 )}

//                 {statusBox.data && (
//                   <Box
//                     p={4}
//                     rounded="xl"
//                     bg="blue.50"
//                     borderWidth="1px"
//                     borderColor="blue.100"
//                   >
//                     <Text fontWeight="bold" color="blue.800">
//                       Complaint Status
//                     </Text>

//                     <VStack align="stretch" mt={3} gap={2}>
//                       <Row label="Ticket ID" value={statusBox.data.ticketId} />
//                       <Row
//                         label="Ticket Status"
//                         value={statusBox.data.ticketStatus}
//                       />
//                       <Row
//                         label="Reason"
//                         value={statusBox.data.responseReason}
//                       />
//                       <Row
//                         label="Ticket Type"
//                         value={statusBox.data.ticketType}
//                       />
//                       {statusBox.data.description && (
//                         <Row
//                           label="Description"
//                           value={statusBox.data.description}
//                         />
//                       )}
//                     </VStack>
//                   </Box>
//                 )}
//               </VStack>
//             </Form>
//           );
//         }}
//       </Formik>
//     </BbpsComplainLayout>
//   );
// };

// export default ViewComplaintStatusPage;
